import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, RotateCcw, Shield, Sword, Trophy, Skull } from 'lucide-react';
import confetti from 'canvas-confetti';
import axios from 'axios';

const MAP_BG = 'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/a6ba1e67d75484410a2228b04473d4ffe1eb3c0f0f499b1bc11640bf23a93737.jpeg';
const STAR_URL = 'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/51c2f4d391b08733a1282c5c803c88045bd7e3e5993310b66fbe2ae387825c7c.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AirCombatResultsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const savedRef = useRef(false);

  const score = parseInt(searchParams.get('score') || '0', 10);
  const gameType = searchParams.get('type') || 'defence';
  const kills = parseInt(searchParams.get('kills') || '0', 10);
  const won = searchParams.get('won') === 'true';
  const stars = won ? 3 : kills >= 20 ? 2 : kills >= 10 ? 1 : 0;

  useEffect(() => {
    if (!savedRef.current) {
      savedRef.current = true;
      axios.post(`${API}/aircombat/results`, {
        player_name: localStorage.getItem('gaurik_player_name') || 'Player',
        score,
        total_questions: 30,
        mission: gameType === 'attack' ? 'Attack' : 'Defence',
        kills,
        won,
      }).catch(console.error);
    }
    if (won) {
      const end = Date.now() + 3000;
      const frame = () => {
        confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#FF9933', '#FFFFFF', '#138808'] });
        confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#FF9933', '#FFFFFF', '#138808'] });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [won, score, gameType, kills]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gray-950 relative">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${MAP_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />

      <div className="relative z-10 bg-gray-900/90 backdrop-blur-md border-2 border-gray-700 rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
        {won ? (
          <Trophy className="w-14 h-14 mx-auto mb-3 text-yellow-400" />
        ) : (
          <Skull className="w-14 h-14 mx-auto mb-3 text-red-500" />
        )}

        <h1 data-testid="air-results-message" className="font-heading text-3xl sm:text-4xl font-black text-white mb-1">
          {won ? 'VICTORY!' : 'SHOT DOWN'}
        </h1>
        <p className="font-body text-sm text-gray-400 mb-5">
          {gameType === 'attack' ? 'Attack' : 'Defence'} Mode — {won ? 'All targets destroyed!' : 'You were shot down.'}
        </p>

        <div className="flex justify-center gap-3 mb-5" data-testid="air-results-stars">
          {[1, 2, 3].map((i) => (
            <img key={`star-${i}`} src={STAR_URL} alt={`star ${i}`}
              className={`w-14 h-14 animate-star-in ${i <= stars ? '' : 'grayscale opacity-30'}`}
              style={{ animationDelay: `${i * 250}ms` }}
            />
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-gray-800 rounded-xl p-3">
            <p className="font-body text-xs text-gray-500">Score</p>
            <p data-testid="air-results-score" className="font-heading text-2xl font-black text-white">{score}</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-3">
            <p className="font-body text-xs text-gray-500">Kills</p>
            <p className="font-heading text-2xl font-black text-white">{kills}/30</p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button data-testid="air-retry"
            onClick={() => navigate(`/air-combat/play?type=${gameType}`)}
            className="bg-green-600 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-green-800 active:border-b-0 active:translate-y-[6px] transition-all hover:bg-green-500 flex items-center justify-center gap-2 shadow-lg"
          >
            <RotateCcw className="w-6 h-6" />
            {won ? 'Play Again' : 'Retry'}
          </button>
          <button data-testid="air-modes"
            onClick={() => navigate('/air-combat')}
            className="bg-gray-700 text-white font-black text-base py-3 px-8 rounded-full border-b-[5px] border-gray-900 active:border-b-0 active:translate-y-[5px] transition-all hover:bg-gray-600 flex items-center justify-center gap-2 shadow-lg"
          >
            {gameType === 'attack' ? <Sword className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
            Switch Mode
          </button>
          <button data-testid="air-go-home"
            onClick={() => navigate('/')}
            className="text-gray-500 font-bold text-sm py-2 hover:text-white transition-colors flex items-center justify-center gap-1"
          >
            <Home className="w-4 h-4" /> Back to Studio
          </button>
        </div>
      </div>
    </div>
  );
}
